import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { deleteHttp } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider";
import { IncidentAffectedService } from "./IncidentAffectedServices.types";

export const incidentAffectedServicesUrl = "/incident-affected-services";

export default function useIncidentAffectedServicesHooks() {
    const { isLoading, data: incidentAffectedServices, refetch } = useDotLegalQuery<Array<IncidentAffectedService>>(incidentAffectedServicesUrl);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [incidentAffectedServiceForDeletion, setIncidentAffectedServiceForDeletion] = useState<IncidentAffectedService | undefined>(undefined);
    const [incidentAffectedServiceForUpdate, setIncidentAffectedServiceForUpdate] = useState<IncidentAffectedService | undefined>(undefined);
    const { translateString } = useTranslation();
    const snackBar = useDotLegalSnackbar();
    const { permissions } = useUserContext();
    const masterDataBreadCrumbProvider = useMasterDataBreadCrumbProvider();
    const breadCrumbs = masterDataBreadCrumbProvider.getBreadCrumbs(translateString("incidentAffectedServicesMasterData"));

    const deleteMutation = useDotLegalMutation(deleteIncidentAffectedService, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetch();
                snackBar.show(createElement(Trans, { i18nKey: "itemDeletedSuccess", values: { name: incidentAffectedServiceForDeletion?.name } }));
            } else {
                snackBar.show(
                    createElement(Trans, { i18nKey: "cantDeleteIncidentAffectedService", values: { name: incidentAffectedServiceForDeletion?.name } }),
                    "warning"
                );
            }
            setIncidentAffectedServiceForDeletion(undefined);
        },
    });

    function deleteIncidentAffectedService(id: string) {
        return deleteHttp(`${incidentAffectedServicesUrl}/${id}`);
    }

    const onCreateClick = () => {
        setShowCreateDialog(true);
    };

    const onDialogClose = () => {
        setIncidentAffectedServiceForUpdate(undefined);
        setShowCreateDialog(false);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDelete = () => {
        deleteMutation.mutate(incidentAffectedServiceForDeletion!.id);
    };

    const getMenuItems = (incidentAffectedService: IncidentAffectedService) => {
        const menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => setIncidentAffectedServiceForUpdate(incidentAffectedService),
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => setIncidentAffectedServiceForDeletion(incidentAffectedService),
            });
        }

        return menuItems;
    };

    return {
        isLoading,
        incidentAffectedServices,
        breadCrumbs,
        getMenuItems,
        canManageMasterData: permissions.canManageMasterData,
        showCreateDialog,
        onSaved,
        incidentAffectedServiceForDeletion,
        incidentAffectedServiceForUpdate,
        setIncidentAffectedServiceForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        isDeleting: deleteMutation.isLoading,
    };
}
